import React, { useState, useEffect } from 'react';
import CreatableSelect from 'react-select/creatable';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { getDeviceByImei, updateDeviceName, updateDeviceAttribute, getEntryList, uploadEntryList, clearEntryList, getGroups, getRawDevices, updateDeviceRaw } from '../../services/api/deviceApi';
import './DeviceNameUpdate.css';

const DeviceNameUpdate = ({ onUpdateSuccess }) => {
    const [imei, setImei] = useState('');
    const [selectedName, setSelectedName] = useState(null);
    const [nameOptions, setNameOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingNames, setLoadingNames] = useState(true);
    const [entries, setEntries] = useState([]);
    const [loadingEntries, setLoadingEntries] = useState(true);
    const [showEntryPopup, setShowEntryPopup] = useState(false);

    // Bulk Actions state
    const [showBulkPopup, setShowBulkPopup] = useState(false);
    const [groups, setGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);
    const [bulkProgressMsg, setBulkProgressMsg] = useState('');

    // Load entry list from API
    useEffect(() => {
        const fetchEntries = async () => {
            try {
                const data = await getEntryList();
                if (data && data.length > 0) {
                    // Sort by PositionNumber (numeric sorting)
                    const sortedData = [...data].sort((a, b) => {
                        return String(a.PositionNumber || '').localeCompare(String(b.PositionNumber || ''), undefined, { numeric: true });
                    });

                    setEntries(sortedData);

                    // Create dropdown options from entries
                    const options = sortedData.map(entry => {
                        const dr = entry.Driver || '';
                        const co = entry.CoDriver || '';
                        const crew = dr && co ? `${dr} / ${co}` : (dr || co || 'Unknown');
                        const label = `${entry.PositionNumber || '?'} - ${crew}`;
                        return {
                            value: label,
                            label: label,
                            competitor: entry.Competitior || entry.Competitor || '' // store competitor info for later
                        };
                    });
                    setNameOptions(options);
                } else {
                    setEntries([]);
                    setNameOptions([]);
                }
            } catch (error) {
                console.error('Failed to load entries:', error);
                toast.error('Failed to load entry list');
            } finally {
                setLoadingEntries(false);
                setLoadingNames(false);
            }
        };
        fetchEntries();
    }, []);

    const handleUpdate = async () => {
        if (!imei.trim()) {
            toast.error('Please enter an IMEI');
            return;
        }

        if (!selectedName) {
            toast.error('Please select a device name');
            return;
        }

        setLoading(true);

        try {
            // Get device ID by IMEI
            const deviceId = await getDeviceByImei(imei);

            // Update device name
            await updateDeviceName(deviceId, selectedName.value, imei);

            // Update device attribute with competitor if one is known
            if (selectedName.competitor) {
                await updateDeviceAttribute(deviceId, 'Competitor', selectedName.competitor);
            }

            toast.success('Device updated successfully!');

            // Clear form
            setImei('');
            setSelectedName(null);

            // Trigger parent refresh
            if (onUpdateSuccess) {
                onUpdateSuccess();
            }
        } catch (error) {
            console.error('Update failed:', error);
            toast.error(error.message || 'Failed to update device');
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                // Parse Excel
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                if (data.length === 0) {
                    toast.error('Excel file is empty or invalid.');
                    return;
                }

                setLoadingEntries(true);
                // Upload
                const result = await uploadEntryList(data);
                if (result.success) {
                    toast.success(result.message || 'Import successful');
                    // Refresh table
                    const newEntries = await getEntryList();
                    if (newEntries && newEntries.length > 0) {
                        const sortedData = [...newEntries].sort((a, b) => {
                            return String(a.PositionNumber || '').localeCompare(String(b.PositionNumber || ''), undefined, { numeric: true });
                        });
                        setEntries(sortedData);

                        const options = sortedData.map(entry => {
                            const dr = entry.Driver || '';
                            const co = entry.CoDriver || '';
                            const crew = dr && co ? `${dr} / ${co}` : (dr || co || 'Unknown');
                            const label = `${entry.PositionNumber || '?'} - ${crew}`;
                            return {
                                value: label,
                                label: label,
                                competitor: entry.Competitior || entry.Competitor || ''
                            };
                        });
                        setNameOptions(options);
                    } else {
                        setEntries([]);
                        setNameOptions([]);
                    }
                } else {
                    toast.error(result.error || 'Import failed');
                }
            } catch (error) {
                console.error('Error parsing or uploading file:', error);
                toast.error('Error importing file');
            } finally {
                setLoadingEntries(false);
                // Reset file input
                e.target.value = null;
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleClearEntries = async () => {
        if (!window.confirm("Are you sure you want to delete ALL entries? This action cannot be undone.")) return;

        setLoadingEntries(true);
        try {
            const result = await clearEntryList();
            if (result.success) {
                toast.success('All entries cleared');
                setEntries([]);
                setNameOptions([]);
                setSelectedName(null);
            } else {
                toast.error(result.error || 'Failed to clear entries');
            }
        } catch (error) {
            console.error('Error clearing entries:', error);
            toast.error('Error clearing entries');
        } finally {
            setLoadingEntries(false);
        }
    };

    // --- Bulk Action Logic ---
    const loadGroupsForBulk = async () => {
        try {
            const grps = await getGroups();
            setGroups(grps || []);
        } catch (error) {
            console.error('Error loading groups:', error);
            toast.error('Failed to load groups for bulk assignment');
        }
    };

    const handleOpenBulkPopup = () => {
        setShowBulkPopup(true);
        setBulkProgressMsg('');
        loadGroupsForBulk();
    };

    const handleResetNames = async () => {
        if (!window.confirm("Are you sure you want to reset ALL device names to their IMEI numbers?")) return;

        setIsBulkProcessing(true);
        setBulkProgressMsg('Fetching devices to reset...');
        try {
            const devices = await getRawDevices();
            setBulkProgressMsg(`Resetting ${devices.length} devices... please wait.`);

            let count = 0;
            for (const device of devices) {
                // Keep the rest of device schema intact, just update 'name' to 'uniqueId'
                const updatedDevice = { ...device, name: device.uniqueId };
                await updateDeviceRaw(updatedDevice);
                count++;
            }

            setBulkProgressMsg(`✓ All ${count} devices have been reset.`);
            toast.success(`Reset ${count} devices successfully.`);
            if (onUpdateSuccess) onUpdateSuccess();
        } catch (error) {
            console.error(error);
            setBulkProgressMsg('Error resetting names.');
            toast.error('Error occurred during reset.');
        } finally {
            setIsBulkProcessing(false);
        }
    };

    const handleAssignToGroup = async () => {
        if (!selectedGroupId) {
            toast.error('Please select a group first!');
            return;
        }

        if (!window.confirm(`Are you sure you want to reassign ALL devices to this group?`)) return;

        setIsBulkProcessing(true);
        setBulkProgressMsg('Fetching devices to reassign...');
        try {
            const devices = await getRawDevices();
            setBulkProgressMsg(`Reassigning ${devices.length} devices... please wait.`);

            let count = 0;
            for (const device of devices) {
                const updatedDevice = { ...device, groupId: parseInt(selectedGroupId, 10) };
                await updateDeviceRaw(updatedDevice);
                count++;
            }

            setBulkProgressMsg(`✓ All ${count} devices have been reassigned to group ${selectedGroupId}.`);
            toast.success(`Reassigned ${count} devices successfully.`);
            if (onUpdateSuccess) onUpdateSuccess();
        } catch (error) {
            console.error(error);
            setBulkProgressMsg('Error reassigning groups.');
            toast.error('Error occurred during reassignment.');
        } finally {
            setIsBulkProcessing(false);
        }
    };
    // -------------------------

    // Custom styles for react-select to match the design
    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            backgroundColor: 'var(--input-bg, white)',
            borderColor: state.isFocused ? '#2563eb' : 'var(--border-color, #d1d5db)',
            borderWidth: '1px',
            borderRadius: '0.5rem',
            padding: '0.375rem',
            paddingLeft: '2.5rem', // Space for search icon
            boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.2)' : 'none',
            minHeight: '42px',
            '&:hover': {
                borderColor: '#2563eb'
            }
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: 'var(--input-bg, white)',
            borderRadius: '0.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            zIndex: 100
        }),
        menuList: (provided) => ({
            ...provided,
            maxHeight: '300px'
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? '#2563eb'
                : state.isFocused
                    ? 'var(--hover-bg, #f1f5f9)'
                    : 'transparent',
            color: state.isSelected ? 'white' : 'var(--text-primary, #0f172a)',
            cursor: 'pointer',
            padding: '10px 12px',
            '&:active': {
                backgroundColor: '#2563eb'
            }
        }),
        singleValue: (provided) => ({
            ...provided,
            color: 'var(--text-primary, #0f172a)'
        }),
        input: (provided) => ({
            ...provided,
            color: 'var(--text-primary, #0f172a)',
            margin: '0'
        }),
        placeholder: (provided) => ({
            ...provided,
            color: 'var(--text-tertiary, #94a3b8)'
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            color: 'var(--text-tertiary, #94a3b8)',
            '&:hover': {
                color: 'var(--text-secondary, #64748b)'
            }
        }),
        indicatorSeparator: () => ({
            display: 'none'
        })
    };

    return (
        <div className="device-name-update">
            <h2 className="update-title">Update Device Name</h2>

            <div className="update-form-inline">
                <div className="form-group-inline">
                    <input
                        type="text"
                        id="imei"
                        className="form-input imei-input"
                        placeholder="IMEI"
                        value={imei}
                        onChange={(e) => setImei(e.target.value)}
                        disabled={loading}
                    />
                </div>

                <div className="form-group-inline select-group">
                    <div className="select-wrapper">
                        <span className="select-search-icon material-symbols-outlined">search</span>
                        <CreatableSelect
                            id="device-name"
                            value={selectedName}
                            onChange={setSelectedName}
                            options={nameOptions}
                            styles={customSelectStyles}
                            placeholder="Type or select..."
                            isSearchable
                            isLoading={loadingNames}
                            isDisabled={loading || loadingNames}
                            className="device-name-select"
                            noOptionsMessage={() => "No matching names found"}
                            formatCreateLabel={(inputValue) => `"${inputValue}"`}
                        />
                    </div>
                </div>

                <div className="form-actions-inline">
                    <button
                        className="update-button small-btn"
                        onClick={handleUpdate}
                        disabled={loading || loadingNames}
                        title="Update / Assign Device Name"
                    >
                        {loading ? (
                            <span className="button-spinner"></span>
                        ) : (
                            <span className="material-symbols-outlined">sync_alt</span>
                        )}
                    </button>
                    <button
                        className="entry-list-btn small-btn"
                        onClick={() => setShowEntryPopup(true)}
                        title="Manage Entry List"
                    >
                        <span className="material-symbols-outlined">list_alt</span>
                    </button>
                    <button
                        className="bulk-actions-btn small-btn"
                        onClick={handleOpenBulkPopup}
                        title="Bulk Actions (Reset & Groups)"
                    >
                        <span className="material-symbols-outlined">group_work</span>
                    </button>
                </div>
            </div>

            {/* Entry List Popup Modal */}
            {showEntryPopup && (
                <div className="entry-popup-overlay">
                    <div className="entry-popup-content">
                        <div className="entry-popup-header">
                            <h2 className="update-title" style={{ marginBottom: 0 }}>Entry List Management</h2>
                            <button className="close-popup-btn" onClick={() => setShowEntryPopup(false)}>
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="update-form" style={{ marginTop: '1rem' }}>
                            <div className="form-group" style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                                <div style={{ flex: 1, minWidth: '250px' }}>
                                    <label className="form-label">
                                        Import from Excel (First row skipped):
                                    </label>
                                    <input
                                        type="file"
                                        accept=".xlsx, .xls, .csv"
                                        onChange={handleFileUpload}
                                        disabled={loadingEntries}
                                        className="form-input"
                                        style={{ padding: '0.375rem', height: 'auto', backgroundColor: 'var(--input-bg, white)' }}
                                    />
                                    <p className="form-hint">
                                        <span className="material-symbols-outlined">info</span>
                                        Row 1 is skipped (header). Expected columns: PositionNumber, Driver, CoDriver, Competitior
                                    </p>
                                </div>
                                <div style={{ paddingTop: '1.25rem' }}>
                                    <button
                                        className="update-button small-btn"
                                        style={{ backgroundColor: '#ef4444' }}
                                        onClick={handleClearEntries}
                                        disabled={loadingEntries || entries.length === 0}
                                        title="Delete all entries"
                                    >
                                        <span className="material-symbols-outlined" style={{ marginRight: '0.5rem', fontSize: '1rem' }}>delete</span>
                                        Clear All Entries
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="entry-list-container popup-table-container">
                            {loadingEntries ? (
                                <div style={{ textAlign: 'center', padding: '1rem' }}>
                                    <span className="button-spinner" style={{ borderColor: '#2563eb', borderRightColor: 'transparent', width: '24px', height: '24px', borderWidth: '3px' }}></span>
                                    <p style={{ marginTop: '0.5rem', color: 'var(--text-secondary, #64748b)' }}>Loading entries...</p>
                                </div>
                            ) : entries.length > 0 ? (
                                <table className="entry-table">
                                    <thead>
                                        <tr>
                                            <th>Pos</th>
                                            <th>Driver</th>
                                            <th>Co-Driver</th>
                                            <th>Competitor</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {entries.map((entry, index) => (
                                            <tr key={entry.id || index}>
                                                <td>{entry.PositionNumber}</td>
                                                <td>{entry.Driver}</td>
                                                <td>{entry.CoDriver}</td>
                                                <td>{entry.Competitior}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p style={{ textAlign: 'center', color: 'var(--text-secondary, #64748b)', padding: '2rem 0' }}>No entries found. Please import an Excel file.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Actions Popup Modal */}
            {showBulkPopup && (
                <div className="entry-popup-overlay">
                    <div className="entry-popup-content" style={{ maxWidth: '600px' }}>
                        <div className="entry-popup-header">
                            <h2 className="update-title" style={{ marginBottom: 0 }}>Bulk Device Management</h2>
                            <button className="close-popup-btn" onClick={() => setShowBulkPopup(false)} disabled={isBulkProcessing}>
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="update-form" style={{ padding: '1.5rem' }}>
                            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                                <label htmlFor="groupSelect" className="form-label" style={{ fontWeight: '600' }}>
                                    Target Group Assignment:
                                </label>
                                <select
                                    id="groupSelect"
                                    className="form-input"
                                    value={selectedGroupId}
                                    onChange={(e) => setSelectedGroupId(e.target.value)}
                                    disabled={isBulkProcessing || groups.length === 0}
                                    style={{ padding: '0.5rem', backgroundColor: 'var(--input-bg, white)' }}
                                >
                                    <option value="">{groups.length === 0 ? 'Loading groups...' : '-- Select a group --'}</option>
                                    {groups.map(g => (
                                        <option key={g.id} value={g.id}>{g.name}</option>
                                    ))}
                                </select>
                                <button
                                    className="update-button"
                                    style={{ marginTop: '0.75rem', backgroundColor: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    onClick={handleAssignToGroup}
                                    disabled={isBulkProcessing || !selectedGroupId}
                                >
                                    <span className="material-symbols-outlined" style={{ marginRight: '0.5rem' }}>extension</span>
                                    Assign ALL Devices to Selected Group
                                </button>
                            </div>

                            <hr style={{ margin: '1.5rem 0', borderColor: 'var(--border-color, #e2e8f0)', borderTopWidth: '1px' }} />

                            <div className="form-group">
                                <label className="form-label" style={{ fontWeight: '600' }}>
                                    System Reset:
                                </label>
                                <button
                                    className="update-button"
                                    style={{ backgroundColor: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    onClick={handleResetNames}
                                    disabled={isBulkProcessing}
                                >
                                    <span className="material-symbols-outlined" style={{ marginRight: '0.5rem' }}>restart_alt</span>
                                    Reset ALL Device Names to IMEI
                                </button>
                                <p className="form-hint" style={{ marginTop: '0.5rem' }}>
                                    <span className="material-symbols-outlined" style={{ color: '#ef4444' }}>warning</span>
                                    This will permanently overwrite the names of EVERY device!
                                </p>
                            </div>

                            {bulkProgressMsg && (
                                <div style={{
                                    marginTop: '1.5rem',
                                    padding: '1rem',
                                    backgroundColor: isBulkProcessing ? '#fef3c7' : '#dcfce7',
                                    color: isBulkProcessing ? '#92400e' : '#166534',
                                    borderRadius: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    fontWeight: '500'
                                }}>
                                    {isBulkProcessing && (
                                        <span className="button-spinner" style={{ borderColor: 'currentColor', borderRightColor: 'transparent', width: '20px', height: '20px' }}></span>
                                    )}
                                    {!isBulkProcessing && (
                                        <span className="material-symbols-outlined">check_circle</span>
                                    )}
                                    {bulkProgressMsg}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeviceNameUpdate;
